import { type FC, type ReactNode, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { getAriaLabel } from 'shared/lib'
import { Typography } from 'shared/ui'
import type { IIVRTransition } from 'entities/Message'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { SendToVoicemail } from 'widgets/MessageCard/ui/CallContent/CallIvr/SendToVoicemail'
import { LetTheCallTrough } from 'widgets/MessageCard/ui/CallContent/CallIvr/LetTheCallTrough'
import { ForwardToExternalNumber } from 'widgets/MessageCard/ui/CallContent/CallIvr/ForwardToExternalNumber'
import { ForwardToAnotherInbox } from 'widgets/MessageCard/ui/CallContent/CallIvr/ForwardToAnotherInbox'
import { ForwardToInboxMembers } from 'widgets/MessageCard/ui/CallContent/CallIvr/ForwardToInboxMembers'
import { SendSms } from 'widgets/MessageCard/ui/CallContent/CallIvr/SendSms'
import type { ConversationMessagesStore } from 'widgets/ConversationMessages'
import styles from './styles.module.scss'

type IIvrContentProps = {
  info: IIVRTransition
  conversationsHistory: boolean
  conversationMessagesStore?: ConversationMessagesStore
}

export const IvrContent: FC<IIvrContentProps> = observer(
  ({ info, conversationsHistory, conversationMessagesStore }) => {
    const { key, destination, value } = info

    const text: ReactNode = useMemo(() => {
      if (destination === 'external_number' && typeof value === 'string') {
        return <ForwardToExternalNumber number={value} />
      }
      if (destination === 'inbox_members' && Array.isArray(value)) {
        return <ForwardToInboxMembers info={value} />
      }
      if (destination === 'another_inbox' && value instanceof Inbox) {
        return <ForwardToAnotherInbox info={value} />
      }
      if (destination === 'let_the_call_through') {
        return <LetTheCallTrough />
      }
      if (destination === 'send_to_voicemail') {
        return <SendToVoicemail />
      }
      if (destination === 'send_sms') {
        return <SendSms />
      }
      return null
    }, [destination])

    const isKeyPressed = destination && key?.length

    return (
      <div
        className={classNames(styles.wrapContent, {
          [styles.conversationsHistoryView]: conversationsHistory,
          [styles.openConversationView]: conversationMessagesStore?.isOpenConversationVariant,
        })}
      >
        <div className={styles.title} aria-label={getAriaLabel('ivr_properties')}>
          <Typography variant={'body-sm-regular'} ariaLabel={'ivr_title'}>
            IVR
          </Typography>
          <span className={styles.divider} />
          <Typography variant={'body-sm-regular'} ariaLabel={'ivr_key'}>
            {isKeyPressed ? `Key ${key}` : 'No key pressed'}
          </Typography>
        </div>
        {isKeyPressed && <div>{text}</div>}
      </div>
    )
  }
)
